import React, { useCallback, useMemo } from 'react'
import { Integrations } from '../../../../../pages/settings/components/integrations'
import { IntegrationsContext } from '../../../../../pages/settings/components/IntegrationsContext'
import { useOnboardingIntegrationsContext } from './useOnboardingIntegrationsContext'
import ConfigureDialog from '../../../../../pages/settings/components/configureDialog'
import AdConnectDialog from '../../../../../pages/settings/components/adConnectDialog'
import OktaConnectDialog from '../../../../../pages/settings/components/oktaConnectDialog'
import { useOnboardingContext } from '../../../context/OnboardingContext'
import DeleteDialog from '../../../../../common/components/DeleteDialog'
import { Button, Box } from '@mui/material'
import { viewStyles } from '../VerifyDomainsView/VerifyDomainsView.styles'

export default function IntegrationsTab() {
  const {
    handleCompleteStep,
    OnboardingStepType,
    allCards: allSteps,
    currentStepDetails,
    closeDrawer
  } = useOnboardingContext()

  const onSuccessfulSyncForOnboarding = useCallback(() => {
    const importStep = allSteps?.find(step => step.apiType === OnboardingStepType.IMPORT_EMPLOYEES)

    if (importStep && importStep.status !== 'COMPLETED' && handleCompleteStep && OnboardingStepType?.IMPORT_EMPLOYEES) {
      handleCompleteStep(OnboardingStepType.IMPORT_EMPLOYEES)
    }
  }, [handleCompleteStep, OnboardingStepType, allSteps])

  const contextFromHook = useOnboardingIntegrationsContext(onSuccessfulSyncForOnboarding)

  const finalIntegrationsContextValue = {
    ...contextFromHook,
    onSuccessfulSyncForOnboarding
  }

  const {
    openAdUpdateDialog,
    adUpdateDialogOpen,
    setAdUpdateDialogOpen,
    disconnect,
    integrationTitles,
    open,
    setOpen,
    selectedConnector,
    connectors
  } = contextFromHook

  const hasSyncedIntegration = useMemo(() => {
    if (!connectors || connectors.length === 0) return false

    const supportedTypes = ['GOOGLE', 'AD', 'OUTLOOK', 'MICROSOFT']
    return supportedTypes.some(type => connectors.some(connector => connector.provider === type))
  }, [connectors])

  const handleDisconnect = () => {
    if (contextFromHook.selectedConnector) {
      contextFromHook.setOpen(true)
      contextFromHook.setShowConfigureDialog(false)
    }
  }

  const handleDone = () => {
    if (currentStepDetails && handleCompleteStep && OnboardingStepType?.IMPORT_EMPLOYEES) {
      handleCompleteStep(OnboardingStepType.IMPORT_EMPLOYEES)
    }
    closeDrawer()
  }

  const handleAdUpdateSuccess = useCallback(() => {
    setAdUpdateDialogOpen(false)
    contextFromHook.updateConnectors()
    contextFromHook.setShowConfigureDialog(true)
  }, [setAdUpdateDialogOpen, contextFromHook])

  const shouldRenderAdConfigureDialog =
    contextFromHook.showConfigureDialog && contextFromHook.selectedConnector === 'AD'
  const shouldRenderAdUpdateDialog = adUpdateDialogOpen && contextFromHook.selectedConnector === 'AD'
  const shouldRenderOktaDialog = contextFromHook.showConfigureDialog && contextFromHook.selectedConnector === 'OKTA'
  const shouldRenderConfigureDialog =
    contextFromHook.showConfigureDialog &&
    (contextFromHook.selectedConnector === 'GOOGLE' ||
      contextFromHook.selectedConnector === 'OUTLOOK' ||
      contextFromHook.selectedConnector === 'MICROSOFT') &&
    contextFromHook.connectors &&
    contextFromHook.connectors.length > 0

  return (
    <IntegrationsContext.Provider value={finalIntegrationsContextValue}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 190px)',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ flexGrow: 1, height: '100%' }}>
          <Integrations isDescription={false} />
        </Box>

        {hasSyncedIntegration && (
          <Box sx={viewStyles.footer}>
            <Button variant='outlined' onClick={handleDone} sx={viewStyles.doneButton} fullWidth>
              Done
            </Button>
          </Box>
        )}
      </Box>

      {shouldRenderAdConfigureDialog && (
        <ConfigureDialog
          open={contextFromHook.showConfigureDialog}
          onClose={() => contextFromHook.setShowConfigureDialog(false)}
          type={contextFromHook.selectedConnector}
          onDisconnect={handleDisconnect}
          connectors={contextFromHook.connectors}
          updateConnectors={contextFromHook.updateConnectors}
          cooldownConnectors={contextFromHook.cooldownConnectors}
          setCooldownConnectors={contextFromHook.setCooldownConnectors}
          startCooldown={contextFromHook.startCooldown}
          onSuccess={finalIntegrationsContextValue.onSuccessfulSyncForOnboarding}
          onUpdateCredentials={openAdUpdateDialog}
        />
      )}
      {shouldRenderAdUpdateDialog && (
        <AdConnectDialog
          open={adUpdateDialogOpen}
          onClose={() => setAdUpdateDialogOpen(false)}
          selectedConnector={contextFromHook.selectedConnector}
          onSuccess={handleAdUpdateSuccess}
          update={true}
        />
      )}
      {shouldRenderOktaDialog && (
        <OktaConnectDialog
          open={contextFromHook.showConfigureDialog}
          onClose={() => contextFromHook.setShowConfigureDialog(false)}
          selectedConnector={contextFromHook.selectedConnector}
          onSuccess={finalIntegrationsContextValue.onSuccessfulSyncForOnboarding}
        />
      )}
      {shouldRenderConfigureDialog && (
        <ConfigureDialog
          open={contextFromHook.showConfigureDialog}
          onClose={() => contextFromHook.setShowConfigureDialog(false)}
          type={contextFromHook.selectedConnector}
          onDisconnect={handleDisconnect}
          connectors={contextFromHook.connectors}
          updateConnectors={contextFromHook.updateConnectors}
          cooldownConnectors={contextFromHook.cooldownConnectors}
          setCooldownConnectors={contextFromHook.setCooldownConnectors}
          startCooldown={contextFromHook.startCooldown}
          onSuccess={finalIntegrationsContextValue.onSuccessfulSyncForOnboarding}
        />
      )}
      <DeleteDialog
        open={open}
        onClose={() => setOpen(false)}
        str={selectedConnector ? integrationTitles[selectedConnector] : ''}
        onAction={() => {
          disconnect(selectedConnector)
          setOpen(false)
        }}
        mainAction='Disconnect'
        description='Are you sure you want to disconnect this integration? This will stop automatic user and group synchronization.'
      />
    </IntegrationsContext.Provider>
  )
}
